/**
 * 料金表ページのデータ（公式サイト＝定価）。
 *
 * 【SBDCの価格掲載ルール】
 *   ・公式サイト ＝ 定価のみを掲載する。
 *   ・LPサイト ＝ 新規お試しモニター価格を掲載する。
 *   ・複数回コース価格 ＝ Web非掲載（対面カウンセリング時に案内する）。
 *
 * したがって、このファイルには以下を掲載しない。
 *   モニター価格 / キャンペーン価格 / 5回・6回・8回・10回・12回などのコース価格 /
 *   競合他院価格 / 社内検討用価格 / Sパーツ・Lパーツ料金。
 *   料金表は原則として「1回の定価」を表示する。
 *   表示価格はすべて税込（「表示価格はすべて税込です。」の方針に統一）。
 *
 * 【共通構造】
 *   各カテゴリ（タブ）は以下の順で表示する。
 *     1. カテゴリ名 / 2. 短い説明(lead)
 *     3. 施術・部位ごとの料金一覧(groups)
 *     4. 注記(note / ページ共通の priceNotice)
 *     5. 該当施術の詳細ページへの導線(detailLink / group.href / row.href)
 *     6. LINE相談 / 無料カウンセリング予約（PricePanel 側で共通配置）
 */

export type PriceRow = {
  name: string;
  note?: string;
  price: string;
  /** 個別施術詳細ページへのパス（設定時は施術名から遷移できる） */
  href?: string;
  /** お悩みタグ（設定した施術のみ表示。例：ニキビ / シミ・くすみ / たるみ・シワ / 毛穴） */
  tags?: string[];
  /** 対象部位の補足（設定した施術のみ表示） */
  targetAreas?: string;
  /** 回数・セット枠の値（columns を設定した場合に使用。現在は1回定価のみのため未使用） */
  values?: Record<string, string>;
};

export type PriceColumn = {
  key: string;
  label: string;
};

export type PriceGroup = {
  title: string;
  en: string;
  rows: PriceRow[];
  /** グループ見出しの補足（任意） */
  lead?: string;
  /** グループ単位の詳細導線（例：BOTOX / ヒアルロン酸）（任意） */
  href?: string;
  /** 詳細導線のラベル（未指定時は「詳しく見る」） */
  hrefLabel?: string;
  /** 回数・セットの列定義（現在は1回定価のみのため未使用）（任意） */
  columns?: PriceColumn[];
};

export type PriceTab = {
  id: string;
  en: string;
  jp: string;
  lead: string;
  groups: PriceGroup[];
  /**
   * 新規お試しモニター専用LPへの導線（公式サイト内にモニター価格を出さないカテゴリ用）。
   * 料金一覧の後に「定価を確認した後の案内」として独立CTAを表示する。
   */
  monitorLp?: {
    eyebrow: string;
    title: string;
    description: string;
    label: string;
    url: string;
  };
  /** カテゴリ単位の詳細導線（料金表下部に配置）（任意） */
  detailLink?: {
    label: string;
    href: string;
  };
  /** カテゴリの中心となる施術（設定時は料金表上部に「施術名」として明示表示）（任意） */
  featured?: {
    name: string;
    note: string;
    price: string;
  };
  /** カテゴリ単位の注記（料金一覧の直下に「ご案内・注意事項」として表示）（任意） */
  notice?: string[];
};

/** 表示価格はすべて税込 */
const TAX_NOTICE = "表示価格はすべて税込です。";
/** 複数回コースは対面案内のみ */
const COURSE_NOTICE = "複数回のコース料金は、対面カウンセリングにてご案内しています。";
/** 美容医療に関する共通注記（既存サイトの正式な表記） */
const FREE_MEDICAL_NOTICE = "美容医療は自由診療です。施術前に医師より説明を行います。";

/** お肌管理の悩みタグ（既存タグのみ。新しいカテゴリは追加しない） */
const SKIN_ALL = ["ニキビ", "シミ・くすみ", "たるみ・シワ", "毛穴"];
const SKIN_ACNE = ["ニキビ"];
const SKIN_PIGMENT = ["シミ・くすみ"];
const SKIN_LIFTING = ["たるみ・シワ"];

export const priceTabs: PriceTab[] = [
  {
    id: "hair-removal",
    en: "MEDICAL HAIR REMOVAL",
    jp: "医療脱毛",
    lead: "女性・男性、部位ごとに1回の定価をご案内します。複数回のコース料金は、対面カウンセリングにてご案内しています。",
    detailLink: { label: "医療脱毛について詳しく見る", href: "/treatment/hair-removal" },
    notice: [
      TAX_NOTICE,
      COURSE_NOTICE,
      "施術部位やプランによって料金が異なる場合があります。",
      "施術の可否は、診察や肌状態などによって判断します。",
      FREE_MEDICAL_NOTICE,
    ],
    groups: [
      {
        title: "女性",
        en: "LADIES",
        lead: "各部位1回の定価です。",
        rows: [
          { name: "ワキ", price: "11,000円" },
          { name: "うなじ", price: "27,500円" },
          { name: "腕", price: "33,000円" },
          { name: "肘上", price: "22,000円" },
          { name: "肘下", price: "22,000円" },
          { name: "背中・襟足", price: "44,000円" },
          { name: "胸", price: "22,000円" },
          { name: "お腹", price: "22,000円" },
          { name: "胸・お腹", price: "44,000円" },
          { name: "足", price: "44,000円" },
          { name: "膝上", price: "22,000円" },
          { name: "膝下", price: "22,000円" },
          { name: "お尻", price: "22,000円" },
          { name: "VIO", price: "22,000円" },
          { name: "顔", price: "22,000円" },
          { name: "全身（顔・VIOなし）", price: "55,000円" },
          { name: "全身（顔またはVIO）", price: "82,500円" },
          { name: "全身（顔・VIOあり）", price: "99,000円" },
        ],
      },
      {
        title: "男性（顔・首）",
        en: "MENS / FACE・NECK",
        lead: "各部位1回の定価です。",
        rows: [
          { name: "鼻下", price: "16,500円" },
          { name: "アゴ", price: "16,500円" },
          { name: "アゴ下", price: "16,500円" },
          { name: "眉間", price: "16,500円" },
          { name: "額", price: "16,500円" },
          { name: "ホホ", price: "22,000円" },
          { name: "もみあげ", price: "16,500円" },
          { name: "首", price: "22,000円" },
          { name: "うなじ", price: "22,000円" },
        ],
      },
      {
        title: "男性（ボディ）",
        en: "MENS / BODY",
        lead: "各部位1回の定価です。",
        rows: [
          { name: "両ワキ", price: "22,000円" },
          { name: "Vライン", price: "33,000円" },
          { name: "Iライン", price: "33,000円" },
          { name: "Oライン", price: "33,000円" },
          { name: "大腿", price: "33,000円" },
          { name: "膝", price: "22,000円" },
          { name: "膝下", price: "33,000円" },
          { name: "足甲・指", price: "22,000円" },
          { name: "肘上", price: "33,000円" },
          { name: "肘下", price: "33,000円" },
          { name: "手指・甲", price: "22,000円" },
          { name: "うなじ", price: "33,000円" },
          { name: "背中", price: "33,000円" },
          { name: "臀部", price: "33,000円" },
          { name: "胸部", price: "33,000円" },
          { name: "腹部", price: "33,000円" },
        ],
      },
    ],
    // 定価料金表の後に新規お試しモニターLPへの導線を設置（モニター価格は公式サイトに掲載しない）。
    monitorLp: {
      eyebrow: "初めての方へ",
      title: "医療脱毛モニターのご案内",
      description: "初めて当院をご利用になる方を対象としたモニタープランをご用意しています。",
      label: "モニタープランの詳細を見る",
      url: "https://sbscl.biz/990/",
    },
  },
  {
    id: "slim-zero",
    en: "MEDICAL SLIM ZERO",
    jp: "医療痩身",
    lead: "Medical Slim ZEROの各治療の定価をご案内します。プラン・詳細は専用ページでご確認いただけます。",
    groups: [
      {
        title: "治療内容",
        en: "TREATMENT",
        lead: "各治療1回の定価です。",
        rows: [
          { name: "医療痩身注射", note: "1エリア", price: "74,800円", href: "/slim/injection" },
          { name: "RF（ラジオ波痩身）", note: "RF（ラジオ波）", price: "55,000円", href: "/slim/rf" },
          { name: "医療用脂肪冷却", note: "脂肪冷却", price: "154,000円", href: "/slim/cooling" },
          { name: "医療用EMS", note: "EMS", price: "99,000円", href: "/slim/ems" },
        ],
      },
    ],
    // 定価料金表の後に新規お試しモニターLPへの導線を設置（モニター価格は公式サイトに掲載しない）。
    monitorLp: {
      eyebrow: "初めての方へ",
      title: "Medical Slim ZERO モニターのご案内",
      description: "初めて当院をご利用になる方を対象としたモニタープランをご用意しています。",
      label: "モニタープランの詳細を見る",
      url: "https://sbscl.biz/zero/",
    },
    notice: [TAX_NOTICE, "お試しプランのご案内は専用ページで行っています。", FREE_MEDICAL_NOTICE],
  },
  {
    id: "skin",
    en: "SKIN MANAGEMENT",
    jp: "お肌管理",
    lead: "プラズマ・ダーマペン4・ピーリング・光治療の4分類でご案内します。料金は各施術1回の定価です。",
    detailLink: { label: "お肌管理について詳しく見る", href: "/treatment/skin-management" },
    notice: [
      "施術内容や使用する治療方法により、料金が異なる場合があります。",
      "肌状態により、適応や施術内容が変わる場合があります。",
      TAX_NOTICE,
      COURSE_NOTICE,
      FREE_MEDICAL_NOTICE,
    ],
    groups: [
      {
        title: "プラズマ",
        en: "PLASMA",
        rows: [
          {
            name: "ヴィーナス ステムサップ（StemSup-A）",
            note: "プラズマ＋脂肪幹細胞由来培養上清液",
            price: "99,000円",
            href: "/skin/venus-stemsup",
            tags: SKIN_ALL,
          },
          {
            name: "ヴィーナス ステムサップ（StemSup-DP）",
            note: "プラズマ＋歯髄幹細胞由来培養上清液",
            price: "99,000円",
            href: "/skin/venus-stemsup",
            tags: SKIN_ALL,
          },
          {
            name: "ヴィーナス ステムサップ（StemSup-UC）",
            note: "プラズマ＋臍帯幹細胞由来培養上清液",
            price: "143,000円",
            href: "/skin/venus-stemsup",
            tags: SKIN_ALL,
          },
          {
            name: "ヴィーナス ダイヤモンド",
            note: "プラズマ＋リジュラン",
            price: "99,000円",
            href: "/skin/venus-diamond",
            tags: SKIN_ALL,
          },
          {
            name: "プラズマ",
            note: "顔全体・首",
            price: "33,000円",
            href: "/skin/plasma",
            tags: ["ニキビ", "シミ・くすみ", "たるみ・シワ"],
          },
        ],
      },
      {
        title: "ダーマペン4",
        en: "DERMAPEN 4",
        rows: [
          {
            name: "ダーマペン4",
            note: "顔全体",
            price: "55,000円",
          },
          {
            name: "シンデレラ ステムサップ（StemSup-A）",
            note: "ダーマペン4＋脂肪幹細胞由来培養上清液",
            price: "121,000円",
            href: "/skin/cinderella-stemsup",
            tags: SKIN_ALL,
          },
          {
            name: "シンデレラ ステムサップ（StemSup-DP）",
            note: "ダーマペン4＋歯髄幹細胞由来培養上清液",
            price: "121,000円",
            href: "/skin/cinderella-stemsup",
            tags: SKIN_ALL,
          },
          {
            name: "シンデレラ ステムサップ（StemSup-UC）",
            note: "ダーマペン4＋臍帯幹細胞由来培養上清液",
            price: "165,000円",
            href: "/skin/cinderella-stemsup",
            tags: SKIN_ALL,
          },
          {
            name: "シンデレラ ダイヤモンド",
            note: "ダーマペン4＋リジュラン",
            price: "154,000円",
            href: "/skin/cinderella-diamond",
            tags: SKIN_ALL,
          },
          {
            name: "ヴェルヴェットスキン",
            note: "ダーマペン4＋コラーゲンピール",
            price: "99,000円",
            href: "/skin/velvet-skin",
            tags: ["ニキビ", "シミ・くすみ", "毛穴"],
          },
        ],
      },
      {
        title: "ラクトピール",
        en: "PEELING",
        rows: [
          { name: "顔全体", price: "22,000円", href: "/skin/lacto-peel", tags: SKIN_PIGMENT },
          { name: "胸部／デコルテ", price: "33,000円", tags: SKIN_PIGMENT },
          { name: "背中上部または下部", price: "33,000円", tags: SKIN_PIGMENT },
          { name: "臀部", price: "33,000円", tags: SKIN_PIGMENT },
          { name: "上腕", price: "33,000円", tags: SKIN_PIGMENT },
          { name: "両ワキ", price: "33,000円", tags: SKIN_PIGMENT },
        ],
      },
      {
        title: "コラーゲンピール",
        en: "PEELING",
        rows: [
          { name: "顔全体", price: "33,000円", href: "/skin/collagen-peel", tags: SKIN_LIFTING },
          { name: "首", price: "33,000円", tags: SKIN_LIFTING },
          { name: "デコルテ", price: "33,000円", tags: SKIN_LIFTING },
        ],
      },
      {
        title: "MIXピール",
        en: "PEELING",
        rows: [
          { name: "顔全体", price: "22,000円", href: "/skin/mix-peel", tags: SKIN_ACNE },
          { name: "胸部／デコルテ", price: "33,000円", tags: SKIN_ACNE },
          { name: "背中上部または下部", price: "33,000円", tags: SKIN_ACNE },
          { name: "臀部", price: "33,000円", tags: SKIN_ACNE },
          { name: "上腕", price: "33,000円", tags: SKIN_ACNE },
          { name: "両ワキ", price: "33,000円", tags: SKIN_ACNE },
        ],
      },
      {
        title: "ホワイトフォトフェイシャル",
        en: "PHOTO FACIAL",
        lead: "2つの組み合わせのご案内です。",
        rows: [
          {
            name: "フォト＋MIXピール",
            price: "77,000円",
            href: "/skin/white-photo-facial",
            tags: SKIN_ALL,
          },
          {
            name: "フォト＋乳酸ピール",
            price: "77,000円",
            href: "/skin/white-photo-facial",
            tags: SKIN_ALL,
          },
        ],
      },
    ],
    // 定価料金表の後にダーマペン4の新規お試しモニターLPへの導線を設置（モニター価格は公式サイトに掲載しない）。
    monitorLp: {
      eyebrow: "初めての方へ",
      title: "ダーマペン4 モニターのご案内",
      description: "初めて当院をご利用になる方を対象としたモニタープランをご用意しています。",
      label: "モニタープランの詳細を見る",
      url: "https://sbscl.biz/dermapen4/",
    },
  },
  {
    id: "regenerative",
    en: "REGENERATIVE MEDICINE",
    jp: "再生医療",
    lead: "StemSup（幹細胞培養上清液）を用いた点滴メニューをご案内します。料金は1回の定価です。",
    featured: { name: "StemSup点滴", note: "幹細胞培養上清液を用いた点滴メニュー", price: "66,000円〜110,000円" },
    detailLink: { label: "StemSup点滴について詳しく見る", href: "/regenerative/stemsup-drip" },
    notice: [
      TAX_NOTICE,
      COURSE_NOTICE,
      FREE_MEDICAL_NOTICE,
    ],
    groups: [
      {
        title: "StemSup点滴",
        en: "STEMSUP IV THERAPY",
        lead: "各製剤1回の定価です。",
        rows: [
          {
            name: "脂肪幹細胞由来培養上清液（StemSup-A）",
            price: "66,000円",
            href: "/regenerative/stemsup-drip",
          },
          {
            name: "歯髄幹細胞由来培養上清液（StemSup-DP）",
            price: "66,000円",
            href: "/regenerative/stemsup-drip",
          },
          {
            name: "臍帯幹細胞由来培養上清液（StemSup-UC）",
            price: "110,000円",
            href: "/regenerative/stemsup-drip",
          },
        ],
      },
    ],
    // 定価料金表の後にStemSup点滴の新規お試しモニターLPへの導線を設置（モニター価格は公式サイトに掲載しない）。
    monitorLp: {
      eyebrow: "初めての方へ",
      title: "StemSup点滴 モニターのご案内",
      description: "初めて当院をご利用になる方を対象としたモニタープランをご用意しています。",
      label: "モニタープランの詳細を見る",
      url: "https://sbscl.biz/saisei/",
    },
  },
  {
    id: "injectables",
    en: "INJECTABLES",
    jp: "注入治療",
    lead: "ボトックスとヒアルロン酸を、部位ごとに1回の定価でご案内します。",
    notice: [
      TAX_NOTICE,
      COURSE_NOTICE,
      "注入量・単位数は目安です。実際の内容は診察時にご案内します。",
      FREE_MEDICAL_NOTICE,
    ],
    groups: [
      {
        title: "ボトックス",
        en: "BOTOX",
        href: "/injectables/botox",
        hrefLabel: "BOTOXについて詳しく見る",
        rows: [
          { name: "額", note: "10単位", price: "33,000円" },
          { name: "眉間", note: "10単位", price: "33,000円" },
          { name: "バニー", note: "10単位", price: "33,000円" },
          { name: "目尻", note: "10単位", price: "33,000円" },
          { name: "たれ目", note: "10単位", price: "33,000円" },
          { name: "人中", note: "10単位", price: "33,000円" },
          { name: "口角", note: "10単位", price: "33,000円" },
          { name: "ガミー", note: "10単位", price: "33,000円" },
          { name: "アゴ", note: "10単位", price: "33,000円" },
          { name: "エラ", note: "60単位", price: "88,000円" },
          { name: "ワキ", note: "両ワキ60単位", price: "88,000円" },
          { name: "肩", note: "60単位", price: "88,000円" },
          { name: "ふくらはぎ", note: "50単位", price: "132,000円" },
        ],
      },
      {
        title: "ヒアルロン酸",
        en: "HYALURONIC ACID",
        href: "/injectables/hyaluronic-acid",
        hrefLabel: "ヒアルロン酸について詳しく見る",
        rows: [
          { name: "涙袋（両目）", note: "～1.0cc", price: "44,000円" },
          { name: "鼻", note: "～1.0cc", price: "44,000円" },
          { name: "唇", note: "～1.0cc", price: "66,000円" },
          { name: "顎（あご）", note: "～1.0cc", price: "66,000円" },
          { name: "首のしわ", note: "1本（1.0cc）1本目", price: "66,000円" },
          { name: "ほうれい線（両側）", note: "～1.0cc", price: "66,000円" },
          { name: "口角（両側）", note: "～1.0cc", price: "66,000円" },
          { name: "小じわ", note: "～1.0cc", price: "66,000円" },
          { name: "額（おでこ）", note: "6.0～10.0cc", price: "275,000円" },
          { name: "こめかみ", note: "2.0～4.0cc", price: "66,000円／1cc" },
        ],
      },
    ],
    // 定価料金表の後にボトックスの新規お試しモニターLPへの導線を設置（モニター価格は公式サイトに掲載しない）。
    // 注入治療全体のモニターLPではないため、CTA名称は「ボトックス モニターのご案内」とする。
    monitorLp: {
      eyebrow: "初めての方へ",
      title: "ボトックス モニターのご案内",
      description: "初めて当院をご利用になる方を対象としたモニタープランをご用意しています。",
      label: "モニタープランの詳細を見る",
      url: "https://sbscl.biz/botox/#reservation",
    },
  },
  {
    id: "eye",
    en: "EYE TREATMENT",
    jp: "二重・目元",
    lead: "現在は二重埋没法を、1回の定価でご案内しています。",
    detailLink: { label: "二重・目元について詳しく見る", href: "/eye/double-eyelid" },
    notice: [
      "目元の施術として、二重埋没法をご案内しています。",
      TAX_NOTICE,
      FREE_MEDICAL_NOTICE,
    ],
    groups: [
      {
        title: "二重埋没法",
        en: "DOUBLE EYELID",
        rows: [
          {
            name: "二重埋没法（2点留め・保証なし）",
            note: "2点留め",
            price: "99,000円",
            href: "/eye/double-eyelid",
          },
          {
            name: "二重埋没法（2点留め・1年保証）",
            note: "2点留め・1年保証",
            price: "148,500円",
            href: "/eye/double-eyelid",
          },
        ],
      },
    ],
    // 定価料金表の後に二重埋没法の新規お試しモニターLPへの導線を設置（モニター価格は公式サイトに掲載しない）。
    monitorLp: {
      eyebrow: "初めての方へ",
      title: "二重埋没法 モニターのご案内",
      description: "初めて当院をご利用になる方を対象としたモニタープランをご用意しています。",
      label: "モニタープランの詳細を見る",
      url: "https://sbscl.biz/futae/",
    },
  },
];